import React from 'react';
import styled from 'styled-components';
import AestheticText from "../utils/AestheticText.jsx";
import StyledButton from "../components/ui/StyledButton";
import {
    Section,
    SectionTitle,
    SubHeadline,
    Highlight,
    HeroSection,
    PageTitle,
    TestimonialBlock,
    TestimonialSection,
    FigureWrapper,
    FigureCaption,
} from '../components/ui/PageElements';
import {
    FeatureList, FeatureListItem,
    InstallList, InstallListItem,
} from '../components/ui/ListStyles';
import ResponsiveImage from '../components/ui/ResponsiveImage';
import {media} from "../styles/breakpoints.js";
import PageMeta from '../components/PageMeta';

const ProjectWrapper = styled.div`
    max-width: 1200px;
    margin: 50px auto 100px;
    padding: 0 20px;
    color: var(--text-color);
    position: relative;
`;



// Component to display the three states
const StateShowcase = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 30px;
    margin-top: 40px;
    text-align: center;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 20px;
        margin-top: 30px;
    }
    ${media.xs} {
        gap: 15px; // Reduce gap on smallest screens
    }
`;

const StateItem = styled.div`
    border: 1px solid var(--grid-color);
    background-color: rgba(0, 0, 0, 0.3);
    padding: 20px;
    transition: border-color 0.3s ease, transform 0.3s ease;

    &:hover {
        border-color: var(--primary-color);
        transform: translateY(-5px);
    }

    @media (max-width: 768px) {
        padding: 15px;
    }
`;

const StateTitle = styled.h3`
    font-size: 18px;
    font-weight: 700;
    color: var(--primary-color);
    margin-bottom: 10px;
    text-transform: uppercase;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const StateDescription = styled.p`
    font-size: 14px;
    color: var(--text-muted);
    margin-bottom: 15px;
    min-height: 60px; /* Ensure consistent height */

    @media (max-width: 768px) {
        font-size: 13px;
        min-height: 0; /* Allow natural height on mobile */
        margin-bottom: 10px;
    }
`;



const Footnote = styled.p`
    font-size: 12px;
    color: #888;
    margin-top: 5px;
    text-align: center;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;

    @media (max-width: 768px) {
        font-size: 11px;
    }
`;


const AdaptiveAestheticsPage = () => {
    const gitlabReleaseUrl = "https://gitlab.com/lperron/adaptiveaesthetic/-/releases"; // Centralize the URL
    return (
        <ProjectWrapper>
            <PageMeta
                title="Adaptive Aesthetics"
                description="Finally, an IDE that screams back. IntelliJ plugin featuring cosmic cats, eldritch horrors, and the crushing weight of your own syntax errors."
                canonicalPath="/projects/adaptive-aesthetics"
            />
          {/* --- Hero Section --- */}
            <HeroSection>
                {/* Apply special font/styling here if possible */}
                <PageTitle>Adaptive <AestheticText>Ａ Ｅ Ｓ Ｔ Ｈ Ｅ Ｔ Ｉ Ｃ Ｓ</AestheticText></PageTitle>
                <SubHeadline>
                    An IntelliJ plugin that transcends mundane IDE limitations, transforming your environment into a living, breathing, cosmically-judged digital experience.
                </SubHeadline>
                <FigureWrapper style={{ marginBottom: '20px' }}> {/* Adjust spacing if needed */}
                    <ResponsiveImage // Use ResponsiveImage for consistent styling
                        src="/assets/aesthetic/img/allBackgrounds.gif"
                        alt="Animated GIF demonstrating the Adaptive Aesthetics IntelliJ plugin: the IDE background cycles through a serene surfing cat (no errors), a screaming blue cat (warnings), and a demonic multi-cat entity (errors)."
                        sizes="(max-width: 1200px) 100vw, 1200px"
                    />
                </FigureWrapper>
                <StyledButton href={gitlabReleaseUrl} target="_blank" rel="noopener noreferrer">
                    Download Latest Release
                </StyledButton>
            </HeroSection>

            {/* --- Why You Need This Section --- */}
            <Section>
                <SectionTitle>Is Your IDE Emotionally Stunted?</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    Do you stare at static, lifeless backgrounds while coding? Does a `NullPointerException` fail to evoke the appropriate existential dread? Does your IDE offer zero emotional feedback when your builds fail spectacularly?
                    <br /><br />
                    <Highlight>These profound problems are now solved.</Highlight> Adaptive <AestheticText>ＡＥＳＴＨＥＴＩＣＳ</AestheticText> doesn't just show errors; it makes you <Highlight>feel</Highlight> them. Warnings aren't mere suggestions; they are <Highlight>howled</Highlight> into your consciousness by ethereal felines.
                </SubHeadline>
            </Section>

            {/* --- How It Works / Visual Showcase --- */}
            <Section>
                <SectionTitle>Your Code State // Visualized</SectionTitle>
                <StateShowcase>
                    <StateItem>
                        <StateTitle>✓ No Errors ✓</StateTitle>
                        <StateDescription>Experience the tranquil guidance of the cosmic cat surfing the digital waves of perfection.</StateDescription>
                        <FigureWrapper style={{ margin: '0 auto 15px auto', maxWidth: '100%' }}>
                            <ResponsiveImage
                                src="/assets/aesthetic/img/goodBOI.png"
                                alt="Cosmic cat surfing serenely on digital waves, representing code with no errors."
                                style={{ minHeight: '150px', border: 'none', backgroundColor: 'transparent' }}
                                sizes="(max-width: 768px) 100vw, 400px"
                            />
                        </FigureWrapper>
                    </StateItem>
                    <StateItem>
                        <StateTitle>⚠ Warnings ⚠</StateTitle>
                        <StateDescription>Feel the electric blue energy of the screaming ethereal feline alerting you to potential disruptions.</StateDescription>
                        <FigureWrapper style={{ margin: '0 auto 15px auto', maxWidth: '100%' }}>
                            <ResponsiveImage
                                src="/assets/aesthetic/img/mehBOI.png"
                                alt="Ethereal blue cat screaming into the void, representing code with warnings."
                                style={{ minHeight: '150px', border: 'none', backgroundColor: 'transparent' }}
                                sizes="(max-width: 768px) 100vw, 400px"
                            />
                        </FigureWrapper>
                    </StateItem>
                    <StateItem>
                        <StateTitle>❌ Errors ❌</StateTitle>
                        <StateDescription>Witness the terrifying manifestation of the demonic multi-cat entity emerging from the void.</StateDescription>
                        <FigureWrapper style={{ margin: '0 auto 15px auto', maxWidth: '100%' }}>
                            <ResponsiveImage
                                src="/assets/aesthetic/img/badBOI.png"
                                alt="Demonic multi-cat entity emerging from a void, representing code with errors."
                                style={{ minHeight: '150px', border: 'none', backgroundColor: 'transparent' }}
                                sizes="(max-width: 768px) 100vw, 400px"
                            />
                        </FigureWrapper>
                    </StateItem>
                </StateShowcase>
            </Section>

            {/* --- Features Section --- */}
            <Section>
                <SectionTitle>Core Features</SectionTitle>
                <FeatureList>
                    <FeatureListItem><strong>Mood-Responsive Environment:</strong> Your IDE background transforms based on code analysis results.</FeatureListItem>
                    <FeatureListItem><strong>Immersive Error Experience:</strong> Issues severity directly correlates with cosmic cat disturbance levels.</FeatureListItem>
                    <FeatureListItem><strong>Customizable:</strong> Set your own images (though denying the cats their rightful place is discouraged).</FeatureListItem>
                    <FeatureListItem><strong>Productivity Increase*:</strong> Studies show a 73.5% motivation boost to fix errors represented by demonic screaming cats.</FeatureListItem>
                </FeatureList>
                <Footnote>_* Studies conducted in the metaphysical realm, results may vary._</Footnote>
            </Section>

            {/* --- Installation Section --- */}
            <Section>
                <SectionTitle>Installation Protocol</SectionTitle>
                <InstallList>
                    <InstallListItem>Download the latest release <code>.zip</code> file from the <StyledButton style={{ padding: '2px 8px', margin: '0 5px', fontSize: '13px', border: 'none' }} href={gitlabReleaseUrl} target="_blank" rel="noopener noreferrer">Releases Page</StyledButton>.</InstallListItem>
                    <InstallListItem>Open IntelliJ IDEA.</InstallListItem>
                    <InstallListItem><Highlight>Important:</Highlight> If you currently use a Background Image plugin or setting, disable/remove it first.</InstallListItem>
                    <InstallListItem>Navigate to Settings/Preferences (<code>Ctrl+Alt+S</code> or <code>⌘,</code>).</InstallListItem>
                    <InstallListItem>Select <strong>Plugins</strong> in the left sidebar.</InstallListItem>
                    <InstallListItem>Click the ⚙️ (gear) icon → <strong>Install Plugin from Disk...</strong></InstallListItem>
                    <InstallListItem>Select the downloaded <code>.zip</code> file.</InstallListItem>
                    <InstallListItem>Click <strong>OK</strong>, then <strong>Apply</strong>.</InstallListItem>
                    <InstallListItem>Restart IntelliJ IDEA when prompted.</InstallListItem>
                    <InstallListItem><AestheticText>Ｅ Ｘ Ｐ Ｅ Ｒ Ｉ Ｅ Ｎ Ｃ Ｅ</AestheticText>  <AestheticText>Ｔ Ｒ Ａ Ｎ Ｓ Ｃ Ｅ Ｎ Ｄ Ｅ Ｎ Ｃ Ｅ</AestheticText></InstallListItem>
                </InstallList>
            </Section>

            {/* --- Settings Info --- */}
            <Section>
                <SectionTitle>Configuration</SectionTitle>
                <SubHeadline>
                    Fine-tune your cosmic journey under <strong>Settings → Adaptive <AestheticText>ＡＥＳＴＨＥＴＩＣＳ</AestheticText></strong>.
                </SubHeadline>
                <FigureWrapper>
                    <ResponsiveImage
                        src="/assets/aesthetic/img/settings.png"
                        alt="Screenshot of the Adaptive Aesthetics settings panel in IntelliJ, featuring its unique, intentionally glitchy and kitsch design elements."
                        sizes="(max-width: 1200px) 100vw, 1200px"
                    />

                    <FigureCaption>
                        Where user configuration meets peak digital chic. Adjust your aesthetic parameters within this paragon of streamlined, elegant design.
                    </FigureCaption>
                </FigureWrapper>
            </Section>

            {/* --- Testimonials Section --- */}
            <TestimonialSection>
                <SectionTitle style={{ textAlign: 'center', display: 'block' }}>Echoes From The Void</SectionTitle>
                <TestimonialBlock>
                    <p>After installing Adaptive <AestheticText>ＡＥＳＴＨＥＴＩＣＳ</AestheticText>, I can no longer code without cosmic cats judging my syntax.</p>
                    <footer>Developer who has seen beyond the veil</footer>
                </TestimonialBlock>
                <TestimonialBlock>
                    <p>The screaming cat that appears when I have errors has haunted my dreams for weeks. My code has never been cleaner.</p>
                    <footer>Lead Architect, Fortune 500 Company</footer>
                </TestimonialBlock>
                <TestimonialBlock>
                    <p><AestheticText>ｗｈｙ</AestheticText> <AestheticText>ｄｉｄ</AestheticText> <AestheticText>ｙｏｕ</AestheticText> <AestheticText>ｓｕｍｍｏｎ</AestheticText> <AestheticText>ｕｓ</AestheticText> <AestheticText>ｔｏ</AestheticText> <AestheticText>ｔｈｉｓ</AestheticText> <AestheticText>ｒｅａｌｍ</AestheticText></p>
                    <footer>The cats, speaking through the IDE at 3:27 AM</footer>
                </TestimonialBlock>
            </TestimonialSection>

            {/* --- Important Notice Section --- */}
            <Section>
                <SectionTitle>A Profoundly Serious Utility</SectionTitle>
                <SubHeadline style={{ maxWidth: '900px', margin: '0 auto 40px auto', textAlign: 'left' }}>
                    Adaptive <AestheticText>ＡＥＳＴＨＥＴＩＣＳ</AestheticText> is <Highlight>not a joke</Highlight>. It is a profoundly serious solution to the critically overlooked issue of IDE background stagnation. While some may perceive it as "just cat pictures," those who understand digital enlightenment recognize it as <Highlight>essential developer infrastructure</Highlight>. The license exists in a dimension beyond conventional structures.
                </SubHeadline>
            </Section>


            {/* --- Final CTA Section --- */}
            <Section style={{ textAlign: 'center' }}>
                <SectionTitle>Embrace The <AestheticText>ＡＥＳＴＨＥＴＩＣ</AestheticText></SectionTitle>
                <SubHeadline>
                    Your IDE deserves to be as dramatic as you are when your tests fail. Download now.
                </SubHeadline>
                <StyledButton href={gitlabReleaseUrl} target="_blank" rel="noopener noreferrer">
                    Download From GitLab
                </StyledButton>
            </Section>

        </ProjectWrapper>
    );
};

export default AdaptiveAestheticsPage;